import React, { useState, useMemo, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Trophy, X as XIcon } from 'lucide-react';
import { toast } from 'sonner';
import { addDays, parse, format, isValid } from 'date-fns';
import { cn } from '../../lib/utils';
import { LeadsStore } from '../../lib/leadsStore';
import { DatePopover } from '../ui/date-popover';

/** Add `n` *business* days (Mon–Fri) to an ISO yyyy-MM-dd date. Returns ISO. */
function addBusinessDays(iso, n) {
  if (!iso) return '';
  const start = parse(iso, 'yyyy-MM-dd', new Date());
  if (!isValid(start)) return '';
  let d = start;
  let added = 0;
  while (added < n) {
    d = addDays(d, 1);
    const day = d.getDay();
    if (day !== 0 && day !== 6) added += 1;
  }
  return format(d, 'yyyy-MM-dd');
}

/**
 * FundedConfirmModal — final-mile gate before a deal moves from the
 * pipeline into the Fundings ledger. Surfaces the closing date that
 * will stamp `fundedAt` and lets the broker fix it if it slipped.
 *
 * Used from both:
 *   • Pipeline row stage picker
 *   • Lead Profile loan-stage dropdown
 */
export default function FundedConfirmModal({ lead, onClose }) {
  const initialClose = (lead.estimatedClosingDate || '').slice(0, 10);
  const initialFunding = (lead.fundingDate || '').slice(0, 10);
  const [closeDate, setCloseDate] = useState(initialClose);
  // Funding date defaults to closing + 3 business days unless one was already
  // captured on the deal. Whenever the broker moves the closing date, the
  // funding date follows by default — but they can still type their own.
  const [fundingDate, setFundingDate] = useState(initialFunding || addBusinessDays(initialClose, 3));
  // True once the broker has touched the funding field themselves. After
  // that, we stop auto-overwriting it when the closing date moves.
  const [fundingDirty, setFundingDirty] = useState(!!initialFunding);
  const [saving, setSaving] = useState(false);

  // Auto-track closing → funding (+3 business days) only while the user
  // hasn't manually edited the funding date.
  useEffect(() => {
    if (fundingDirty) return;
    setFundingDate(addBusinessDays(closeDate, 3));
  }, [closeDate, fundingDirty]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !saving) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, saving]);

  const submit = async () => {
    if (saving || !closeDate || !fundingDate) return;
    setSaving(true);
    const patch = { loanStatus: 'funded', fundingDate };
    if (closeDate !== initialClose) patch.estimatedClosingDate = closeDate;
    const updated = await LeadsStore.update(lead.id, patch);
    setSaving(false);
    if (updated) {
      toast.success(`${updated.name} → Funded · moved to Fundings`);
      onClose();
    } else {
      toast.error('Could not mark as funded');
    }
  };

  // Friendly preview string for the funding-month label (drives the
  // Fundings monthly grouping). Same parsing approach as CloseDateCell.
  const monthLabel = useMemo(() => {
    const src = fundingDate || closeDate;
    if (!src) return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(src);
    if (!m) return null;
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    if (isNaN(d)) return null;
    return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  }, [closeDate, fundingDate]);

  return createPortal((
    <div data-testid="funded-confirm-modal" className="fixed inset-0 z-[60] flex items-center justify-center">
      <div className="absolute inset-0 bg-neutral-900/40 backdrop-blur-[2px]" onClick={() => !saving && onClose()} />
      <div className="relative w-[460px] max-w-[94vw] rounded-2xl bg-white border hairline shadow-[0_36px_88px_-32px_rgba(0,0,0,0.45)] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 pt-5 pb-4 border-b hairline-soft flex items-start gap-3">
          <div className="h-10 w-10 rounded-xl bg-emerald-50 border border-emerald-200 inline-flex items-center justify-center shrink-0">
            <Trophy className="h-5 w-5 text-emerald-700" strokeWidth={2.25} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-wider font-bold text-emerald-700">Funding deal</div>
            <h2 className="display text-[19px] font-bold tracking-tight text-neutral-900 mt-0.5 leading-tight">
              Confirm closing date for {lead.name}
            </h2>
            <p className="text-[12.5px] text-neutral-500 mt-1 leading-relaxed">
              This date stamps the funding and lands the deal in your <span className="font-semibold text-neutral-700">{monthLabel || 'monthly'}</span> production ledger.
            </p>
          </div>
          <button
            onClick={() => !saving && onClose()}
            data-testid="funded-confirm-close"
            className="h-8 w-8 inline-flex items-center justify-center rounded-lg text-neutral-400 hover:text-neutral-900 hover:bg-neutral-100 transition"
          >
            <XIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 space-y-4">
          <div>
            <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-500 mb-1.5 block">Closing date</span>
            <DatePopover
              testId="funded-confirm-date"
              value={closeDate}
              onChange={setCloseDate}
              placeholder="Pick closing date"
            />
            {!initialClose && (
              <p className="mt-2 text-[11.5px] text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
                Heads up — no closing date is set yet. Pick one before funding.
              </p>
            )}
          </div>
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Funding date</span>
              {!fundingDirty && closeDate && (
                <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                  Auto · closing + 3 business days
                </span>
              )}
            </div>
            <DatePopover
              testId="funded-confirm-funding-date"
              value={fundingDate}
              onChange={(v) => { setFundingDate(v); setFundingDirty(true); }}
              placeholder="Pick funding date"
            />
            {fundingDirty && (
              <button
                type="button"
                data-testid="funded-confirm-funding-reset"
                onClick={() => { setFundingDirty(false); setFundingDate(addBusinessDays(closeDate, 3)); }}
                className="mt-1.5 text-[11px] font-semibold text-neutral-500 hover:text-neutral-900 transition"
              >
                Reset to closing + 3 business days
              </button>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 bg-neutral-50/60 border-t hairline-soft flex items-center justify-end gap-2">
          <button
            data-testid="funded-confirm-cancel"
            onClick={() => !saving && onClose()}
            className="h-9 px-4 rounded-lg border hairline bg-white text-[12.5px] font-bold text-neutral-700 hover:border-neutral-400 transition"
          >
            Cancel
          </button>
          <button
            data-testid="funded-confirm-submit"
            onClick={submit}
            disabled={!closeDate || !fundingDate || saving}
            className={cn(
              'h-9 px-4 rounded-lg text-[12.5px] font-bold transition inline-flex items-center gap-1.5',
              closeDate && fundingDate && !saving
                ? 'bg-emerald-600 text-white hover:bg-emerald-700 shadow-[0_8px_20px_-10px_rgba(5,150,105,0.7)]'
                : 'bg-neutral-200 text-neutral-400 cursor-not-allowed',
            )}
          >
            <Trophy className="h-3.5 w-3.5" strokeWidth={2.5} />
            {saving ? 'Funding…' : 'Mark as Funded'}
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}
